import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Roles } from 'src/app/shared/enums/Roles.enum';

/**
 * ⚠ **FLIP THIS TO `true` WHEN YOU ARE READY TO ROUTE INVESTORS TO V5.**
 *
 * One switch for every environment — staging and production both read it, so flipping it once
 * turns the hand-off on everywhere. Leave it `false` until the edge is ready.
 *
 * ## Why it exists
 *
 * This guard is only the BROWSER half of a two-part mechanism. The other half is the CloudFront
 * function `routing-investors-to-new-app`, which reads the `x-app-target` cookie and serves
 * `/investor-app/index.html` instead of v3's. Neither half is useful alone.
 *
 * With the function detached (its state today on BOTH distributions) and this set to `true`, the
 * guard forces a document request, the edge hands it straight back to v3, and the investor gets a
 * FULL PAGE RELOAD for nothing. Not a crash — the hand-off marker below stops it looping — but it
 * happens on every navigation between the four investor routes, which are an investor's main
 * screens. Pure cost, zero benefit.
 *
 * ## Turning it on, in order
 *
 * 1. Attach the function to the distribution (`E3IMJ8RG3O3NQ1` staging, `E3GGZRB0A8QLUE` prod)
 * 2. Add the `/api/v5/*` behaviour pointing at the ALB, so the investor app can reach its API
 * 3. Deploy external-web into the bucket's `investor-app/` prefix
 * 4. Set this to `true` and redeploy this app
 *
 * It is a compile-time constant, so step 4 needs a rebuild and redeploy — attaching the function
 * alone will not switch it on.
 */
export const INVESTOR_HANDOFF_ENABLED = false;

/**
 * The paths the investor app owns.
 *
 * ⚠ These two lists MUST stay identical to `newAppRoutes` and `newAppFolders` in the CloudFront
 * function `routing-investors-to-new-app`. The edge decides which app a URL belongs to; this guard
 * only makes sure the browser actually ASKS the edge. If the lists drift, a path is either handed
 * off and then served by v3 (a reload loop) or never handed off at all.
 */
export const INVESTOR_ROUTES = ['/', '/my-investments', '/opportunities', '/financial-transactions'];

/**
 * Prefixes the investor app owns — `newAppFolders` in the CloudFront function, and they must match.
 *
 * ⚠ '/financial-transactions/' also covers /donate and /invest, which the investor app does not
 * have screens for yet. Until it does, those two hand off and land on its "not found". That is the
 * accepted cost of keeping one prefix instead of listing /withdraw and /deposit exactly; the edge
 * does the same, so at least both directions behave alike.
 */
export const INVESTOR_ROUTE_PREFIXES = ['/opportunities/', '/financial-transactions/'];

/** Name of the cookie the login effect sets; the CloudFront function reads the same one. */
export const APP_TARGET_COOKIE = 'x-app-target';

/**
 * Writes the cookie the CloudFront function reads.
 *
 * Deliberately a session cookie (no `expires`/`max-age`): it must not outlive the browser, because
 * a stale one would send the NEXT person who logs in on this machine to the investor app. The cost
 * is that it dies on browser close while the login token in localStorage survives — which is why
 * `restoreSession$` re-asserts it on every start.
 */
export function setAppTargetCookie(target: string): void {
  document.cookie = `${APP_TARGET_COOKIE}=${target}; path=/; secure; samesite=lax`;
}

/** Deletes it, so the edge serves v3 again. Same expiry the investor app uses on its own logout. */
export function clearAppTargetCookie(): void {
  document.cookie = `${APP_TARGET_COOKIE}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
}

/** Guards against a reload loop if the edge function is ever detached or misconfigured. */
const HANDOFF_MARKER = 'investorsHandoffAttempt';

/**
 * **Sends an investor out of this app when they navigate to a path the investor app owns.**
 *
 * ## Why this is needed at all
 *
 * The CloudFront function that chooses between this app and the investor app runs on a **document
 * request**. It never
 * sees an in-app Angular navigation — so once this app is loaded in the browser, clicking
 * *Oportunidades* is handled by v3's own router, no request leaves the browser, and v3 renders its
 * own screen. The investor silently stays on v3 for the rest of the session.
 *
 * Login already gets this right: `auth.effects.ts` sets the cookie and then uses
 * `window.location.href` rather than `navigateByUrl`, which is exactly why the first landing works.
 * This guard applies the same rule to every other way of reaching those paths.
 *
 * ## Why a guard rather than fixing each call site
 *
 * There are many: the side menu, the home redirect, post-investment redirects, `routerLink`s in
 * templates, the profile-completion guard. A guard catches all of them, including the ones added
 * later by someone who has never heard of the edge function.
 *
 * ## Three conditions, all required
 *
 * 1. **The path belongs to the investor app** — one of INVESTOR_ROUTES exactly, or under one of
 *    INVESTOR_ROUTE_PREFIXES. The edge matches the same way, so this does too.
 * 2. **The investor cookie is present.** Without it the edge would serve v3 anyway, so a full page
 *    load would reload the same screen for nothing.
 * 3. **This page is not itself the result of a hand-off for this path that the edge sent back to
 *    v3.** If the edge function is detached, v3 would be served again and the guard would fire
 *    again — an endless reload. The marker breaks that after one try and the investor simply stays
 *    on v3, which is a visible bug rather than a hung browser. Recognised by POSITION, not by a
 *    time window: a slow reload is still a bounce.
 */
@Injectable({ providedIn: 'root' })
export class InvestorsHandoffGuard implements CanActivate {
  canActivate(_next: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    // Kill switch — see INVESTOR_HANDOFF_ENABLED above. While it is off this guard is inert and
    // v3 keeps serving the four investor routes with ordinary client-side navigation, exactly as
    // it did before any of this existed.
    if (!this.handoffEnabled()) {
      return true;
    }

    const path = state.url.split('?')[0].split('#')[0] || '/';

    const ownedByInvestorApp =
      INVESTOR_ROUTES.indexOf(path) !== -1 ||
      INVESTOR_ROUTE_PREFIXES.some(prefix => path.indexOf(prefix) === 0);

    if (!ownedByInvestorApp) {
      return true;
    }

    if (!this.isInvestor()) {
      return true;
    }

    if (this.bouncedBack(path)) {
      return true;
    }

    this.markHandoff(path);

    // The FULL url, query string and fragment included — dropping them would lose filters, tabs and
    // campaign parameters on the way across. `path` above is only for matching.
    this.navigateTo(state.url);
    return false;
  }

  private isInvestor(): boolean {
    return document.cookie
      .split(';')
      .map(part => part.trim())
      .some(part => part === `${APP_TARGET_COOKIE}=${Roles.INVESTOR}`);
  }

  /**
   * True when THIS page was loaded by our own hand-off for `path` and the edge served v3 anyway
   * (function detached or misconfigured). Recognised by position, not by time: the previous
   * document wrote the marker just before navigating, and the browser came back here at that
   * same path. A slow reload is still a bounce. The marker is consumed so a later attempt is
   * allowed again.
   */
  private bouncedBack(path: string): boolean {
    try {
      if (sessionStorage.getItem(HANDOFF_MARKER) !== path) return false;
      const loadedHere = window.location.pathname.replace(/\/+$/, '') || '/';
      const bounced = loadedHere === path;
      if (bounced) sessionStorage.removeItem(HANDOFF_MARKER);
      return bounced;
    } catch {
      return false;
    }
  }

  private markHandoff(path: string): void {
    try {
      sessionStorage.setItem(HANDOFF_MARKER, path);
    } catch {
      // Private browsing can throw. Losing the loop-breaker is acceptable; blocking is not.
    }
  }

  /** A real document request; `navigateByUrl` would keep the browser inside v3. */
  protected navigateTo(url: string): void {
    window.location.href = url;
  }

  /** Seam so specs can exercise the guard with the hand-off switched on. */
  protected handoffEnabled(): boolean {
    return INVESTOR_HANDOFF_ENABLED;
  }
}
